import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'
import api from '../services/api'

const PermissionsContext = createContext()

export const usePermissions = () => {
  const context = useContext(PermissionsContext)
  if (!context) {
    throw new Error('usePermissions deve ser usado dentro de um PermissionsProvider')
  }
  return context
}

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth()
  const [userRoles, setUserRoles] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Carregar roles do usuário
  const loadUserRoles = async () => {
    if (!isAuthenticated || !user) {
      setUserRoles([])
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // Extrair roles do usuário logado
      const roles = user.roles?.map(userRole => userRole.role?.name) || []
      setUserRoles(roles)
    } catch (err) {
      console.error('Erro ao carregar roles:', err)
      setError('Erro ao carregar roles do usuário')
      setUserRoles([])
    } finally {
      setLoading(false)
    }
  }

  // Verificar se o usuário tem um role específico
  const hasRole = (role) => {
    return userRoles.includes(role)
  }

  // Verificar se o usuário tem todos os roles especificados
  const hasAllRoles = (requiredRoles) => {
    return requiredRoles.every(role => userRoles.includes(role))
  }

  // Verificar se o usuário tem pelo menos um dos roles especificados
  const hasAnyRole = (requiredRoles) => {
    return requiredRoles.some(role => userRoles.includes(role))
  }

  // Verificar se pode acessar um módulo específico
  const canAccessModule = (module) => {
    if (!isAuthenticated || !userRoles.length) return false
    
    const moduleRoleMap = {
      'onboard': ['inonboard', 'inadmin', 'inmanager'],
      'Expedição': ['inexpedicao', 'inadmin', 'inmanager'],
      'operacional': ['inoperacional', 'inadmin', 'inmanager'],
      'admin': ['inadmin'],
      'manager': ['inmanager', 'inadmin']
    }

    const requiredRoles = moduleRoleMap[module] || []
    return hasAnyRole(requiredRoles)
  }

  // Obter módulos disponíveis para o usuário
  const getAvailableModules = () => {
    if (!isAuthenticated || !userRoles.length) return []
    
    const allModules = ['onboard', 'Expedição', 'operacional', 'admin', 'manager']
    return allModules.filter(module => canAccessModule(module))
  }

  // Carregar roles quando usuário mudar
  useEffect(() => {
    loadUserRoles()
  }, [user, isAuthenticated])

  const value = {
    userRoles,
    loading,
    error,
    hasRole,
    hasAllRoles,
    hasAnyRole,
    canAccessModule,
    getAvailableModules,
    loadUserRoles,
    // Manter compatibilidade com código existente
    permissions: userRoles,
    hasPermission: hasRole,
    hasAllPermissions: hasAllRoles,
    hasAnyPermission: hasAnyRole
  }

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}